'use client'

import { useMemo, useRef, useState } from 'react'

import { useLatestCallback } from '@marketplace-web/shared/ui-helpers'
import { isResponseError, ResponseError } from '@marketplace-web/shared/api-client'

export type FetcherArgs = {
  page: number
}

export type Fetcher<V, A extends FetcherArgs> = (arg: A) => Promise<V | ResponseError>

type PaginatedData<T> = Array<T | undefined>

type Args<Data, Item, ItemId, FArgs extends FetcherArgs> = {
  fetcher: Fetcher<Data, FArgs>
  getIsEndReached: (res: Data) => boolean
  deduplicationUtils?: {
    getItems: (res: Data) => Array<Item>
    getItemId: (item: Item) => ItemId
    replaceItems: (res: Data, items: Array<Item>) => Data
  }
}

export default function usePagination<Data, Item, ItemId, FArgs extends FetcherArgs>({
  getIsEndReached,
  fetcher,
  deduplicationUtils,
}: Args<Data, Item, ItemId, FArgs>) {
  const [data, setData] = useState<PaginatedData<Data>>([])
  const [error, setError] = useState<string | undefined>()

  const [isLoading, setIsLoading] = useState(false)
  const [isEndReached, setIsEndReached] = useState(false)

  const pageRef = useRef(0)
  const ids = useRef<Set<ItemId>>(new Set())

  const filteredData = useMemo(() => {
    return data.filter((item: Data | undefined): item is Data => item !== undefined)
  }, [data])

  const reset = useLatestCallback(() => {
    setData([])
    setIsLoading(false)
    setIsEndReached(false)
    pageRef.current = 0
    ids.current.clear()
  })

  const deduplicateResponse = (res: Data): Data | undefined => {
    if (!deduplicationUtils) return undefined

    const items = deduplicationUtils
      .getItems(res)
      .filter(item => !ids.current.has(deduplicationUtils.getItemId(item)))

    items.map(item => deduplicationUtils.getItemId(item)).forEach(id => ids.current.add(id))

    return deduplicationUtils.replaceItems(res, items)
  }

  const loadPage = useLatestCallback(
    async (opts: FArgs, transform?: (data: Data | ResponseError) => Data | ResponseError) => {
      setError(undefined)

      setIsLoading(true)
      let response: Data | ResponseError = await fetcher(opts)
      setIsLoading(false)

      response = transform?.(response) ?? response

      if (isResponseError(response)) {
        setError(response.message)
      } else {
        const res = response
        setData(prev => {
          const value = [...prev]
          value[opts.page] = res

          return value
        })
      }

      return response
    },
  )

  const loadMore = useLatestCallback((options: Omit<FArgs, keyof FetcherArgs>) => {
    if (isEndReached) return undefined

    pageRef.current += 1
    const newPage = pageRef.current

    return loadPage({ ...options, page: newPage } as FArgs, response => {
      if (isResponseError(response)) {
        return response
      }

      if (getIsEndReached(response)) {
        setIsEndReached(true)
      }

      return deduplicateResponse(response) ?? response
    })
  })

  return { filteredData, data, error, isLoading, loadMore, loadPage, isEndReached, reset }
}
